// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { ConfigurationType } from "@insite/client-framework/Services/ProductServiceV2";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/RfqMyQuotes/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import { QuoteModel } from "@insite/client-framework/Types/ApiModels";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button from "@insite/mobius/Button";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import FolderPlus from "@insite/mobius/Icons/FolderPlus";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowiOSForward from "@store/Library/Components/Toolkit/Icons/ArrowiOSForward";
import OrderActions from "@store/Library/Components/Toolkit/Order/OrderActions";
import GridContainer from "@store/MobiusOverrides/GridContainer/GridContainer";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import constants from "@store/constants";
import { storeGetQuotes, StoreGetQuotesApiParameter } from "@store/Framework/Services/QuoteService";
import quoteAddToCart from "@store/Framework/Store/Store/Pages/Cart/Handlers/QuoteAddToCart";
import AddToCartParameter from "@store/Framework/Store/Store/Pages/Cart/Handlers/QuoteAddToCart";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToQuoteLinkStyles;
}

type Props = OwnProps &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    isSalesPerson: state.context.session.isSalesPerson,
    wishListSettings: getSettingsCollection(state).wishListSettings,
    quotesDataView: getQuotesDataView(state, state.pages.rfqMyQuotes.getQuotesParameter),
    location: getLocation(state),
    rfqMyQuotesPageUrl: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
});

const mapDispatchToProps = {
    loadQuotes,
    updateSearchFields,
    addToCart: makeHandlerChainAwaitable<Parameters<typeof AddToCartParameter>[0], CartLineModel>(quoteAddToCart),
};

export interface ProductAddToQuoteLinkStyles {
    link?: LinkPresentationProps;
    gridItem?: GridItemProps;
    quoteWrapper?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
}

export const productAddToQuoteLinkStyles: ProductAddToQuoteLinkStyles = {
    link: linkGetActionLinkProps(FolderPlus),
    gridItem: { width: [12, 12, 6, 6, 6] },
    quoteWrapper: {
        css: css`
            margin-top: ${pxToRem(22)};
            padding-top: ${pxToRem(22)};
            border-top: ${pxToRem(1)} solid ${getColor("common.border")};
        `,
    },
    viewAllLink: linkGetActionLinkProps(ArrowiOSForward),
};

export const productAddToQuoteLink = productAddToQuoteLinkStyles;

const ProductAddToQuoteLink: React.FC<Props> = ({
    productContext: { productInfo, product },
    wishListSettings,
    labelOverride,
    loadQuotes,
    updateSearchFields,
    extendedStyles,
    quotesDataView,
    history,
    location,
    isSalesPerson,
    rfqMyQuotesPageUrl,
    addToCart,
    session,
    ...otherProps
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [disableButton, setDisableButton] = React.useState(false);
    const [quoteName, setQuoteName] = React.useState("");
    const [quoteNumber, setQuoteNumber] = React.useState("");
    const [selectedQuote, setSelectedQuote] = React.useState("");
    const [getQuotesParameter, setGetQuotesParameter] = React.useState<StoreGetQuotesApiParameter>({
        expand: ["salesList"],
    });
    const [quotesCollection, setQuotesCollection] = React.useState<QuoteModel[] | undefined>([]);
    const isUserPos =
        session.userRoles === null
            ? false
            : session.userRoles.toUpperCase().indexOf(constants.roles.pointOfSale) !== -1;
    const [styles] = React.useState(() => mergeToNew(productAddToQuoteLink, extendedStyles));

    const fetchQuotes = async () => {
        const returnedQuotes = await storeGetQuotes(getQuotesParameter);
        const quotesToAdd = returnedQuotes.quotes?.filter(quote => quote.status === "QuoteCreated");
        setQuotesCollection(quotesToAdd);
    };

    if (session.userRoles === null) {
        return null;
    }

    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            setGetQuotesParameter({
                ...getQuotesParameter,
                _quoteName: quoteName,
                additionalQueryStringParameters: {
                    quoteName,
                },
            } as any);
        }, 1000);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [quoteName]);

    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            setGetQuotesParameter({
                ...getQuotesParameter,
                quoteNumber,
            });
        }, 250);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [quoteNumber]);

    if (!isSalesPerson || !isUserPos || product.configurationType === ConfigurationType.Standard) {
        return null;
    }

    const addToQuoteLinkClickHandler = async () => {
        await fetchQuotes();
        setModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const handleQuoteChangeHandler: SelectProps["onChange"] = event => {
        setSelectedQuote(event.target.value);
    };

    const addToQuoteBtnHandler = async () => {
        setDisableButton(true);
        const cartLine = (await addToCart({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
            quoteId: selectedQuote,
        })) as CartLineModel;

        if (cartLine) {
            toasterContext.addToast({ body: translate("Item Added"), messageType: "success" });
        } else {
            toasterContext.addToast({ body: translate("There was an issue"), messageType: "danger" });
        }
        setDisableButton(false);
    };

    return (
        <>
            <Link {...styles.link} onClick={addToQuoteLinkClickHandler} {...otherProps}>
                {translate("Add to Quote")}
            </Link>
            <Modal
                isOpen={modalIsOpen}
                sizeVariant="small"
                headline={translate("Add to Quote")}
                handleClose={modalCloseHandler}
            >
                <GridContainer>
                    <GridItem {...styles.gridItem}>
                        <TextField
                            value={quoteNumber}
                            label={translate("Filter by Quote #")}
                            onChange={event => setQuoteNumber(event.target.value)}
                            data-test-selector="rfqMyQuotes_quoteNumberFilter"
                        />
                    </GridItem>
                    <GridItem {...styles.gridItem}>
                        <TextField
                            value={quoteName}
                            label={translate("Filter by Name")}
                            onChange={event => setQuoteName(event.target.value)}
                            data-test-selector="rfqMyQuotes_nameFilter"
                        />
                    </GridItem>
                </GridContainer>
                {quotesCollection && (
                    <StyledWrapper {...styles.quoteWrapper}>
                        <Select
                            onChange={handleQuoteChangeHandler}
                            value={selectedQuote}
                            label={translate("Select Quote")}
                        >
                            <option value="">{translate("Select a Quote")}</option>
                            {quotesCollection.map(quote => (
                                <option key={quote.id} value={quote.id}>
                                    {`${quote.orderNumber} ${quote.jobName}`}
                                </option>
                            ))}
                        </Select>
                        <OrderActions>
                            <Button disabled={selectedQuote === "" || disableButton} onClick={addToQuoteBtnHandler}>
                                {translate("Add to Quote")}
                            </Button>
                        </OrderActions>
                        <Link {...styles.viewAllLink} href={rfqMyQuotesPageUrl} {...otherProps}>
                            {translate("View All Quotes")}
                        </Link>
                    </StyledWrapper>
                )}
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withProductContext(ProductAddToQuoteLink)));
